package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun TranslationPane(
    state: TranslationEditorState,
    sidePaddingPx: Int,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    onOpenModal: (EditorModalSurface, EditorFocusRef) -> Unit,
    onOpenOverflow: (EditorFocusRef) -> Unit,
    onCloseOverflow: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * Whether the pane is too narrow for the action bar's full labels.
     *
     * An answer rather than a width, because the pane is sized by its container and not by the
     * window — a media query here would be measuring the wrong box. Defaulted so the many test
     * call sites that pass this pane positionally keep working; the one caller that knows the
     * width sets it.
     */
    compactActions: Boolean = false,
) {
    val selectedRow = state.selectedRow
    val selectedDraft = state.selectedDraft
    val selectedConflict = selectedRow?.let { row -> state.conflicts[row.key.id] }

    // **The editor's main landmark.** This whole route had none — no `<main>`, no `role="main"`,
    // no headings of any level — on the one screen a translator is expected to sit in for eight
    // hours. A screen-reader user got 46 focusables in a flat run with nothing to navigate by.
    //
    // `role` rather than a `<main>` element because this box is a CSS grid child: swapping the tag
    // would be a layout change for a semantics fix, and ARIA exists precisely so it need not be.
    Column(
        EditorPaneStyle.toModifier()
            .then(modifier)
            .attr("role", "main")
            .attr("id", EDITOR_CONTENT_ID)
            .attr("tabindex", "-1")
            .attr("aria-label", t("editor.translation_pane.translation_editor", "Translation editor")),
    ) {
        Div(
            EditorScrollStyle.toModifier()
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .styleModifier { property("height", "auto") }
                .attr("data-editor-scroll", "true")
                .toAttrs(),
        ) {
            if (state.requestedKey.loading || state.requestedKey.failure != null || state.requestedKey.outsideLoadedQueue) {
                EditorContentFrame(sidePaddingPx) { EditorRequestedKeySurface(state) }
            }
            when {
                state.keysLoading && selectedRow == null -> EditorSkeleton(sidePaddingPx)
                selectedRow == null || selectedDraft == null -> EmptyEditor(state, sidePaddingPx)
                else -> EditorSurface(
                    state = state,
                    row = selectedRow,
                    draft = selectedDraft,
                    sidePaddingPx = sidePaddingPx,
                    onCopySource = onCopySource,
                    onInsertText = onInsertText,
                    onOpenUtility = onOpenUtility,
                )
            }
        }
        if (
            !state.keysLoading &&
            selectedRow != null &&
            selectedDraft != null &&
            selectedConflict == null &&
            state.capability.canWriteTranslations
        ) {
            EditorActionBar(
                state = state,
                compactActions = compactActions,
                row = selectedRow,
                draft = selectedDraft,
                onOpenOverflow = onOpenOverflow,
                onCloseOverflow = onCloseOverflow,
                onOpenUtility = onOpenUtility,
                onOpenModal = onOpenModal,
            )
        }
    }
}

@Composable
internal fun EditorSurface(
    state: TranslationEditorState,
    row: KeyRow,
    draft: EditorDraft,
    sidePaddingPx: Int,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
) {
    val remoteIssues = state.qaIssues.filter { it.keyId == row.key.id }
    val editingRow = state.rowForDraft(row, draft)
    val shapeChanged = state.draftShapeChanged(row, draft)
    val findings = mergedQa(inlineQa(editingRow, draft, state.locale), remoteIssues)
    EditorContentFrame(sidePaddingPx) {
        EditorIdentity(state, row, onOpenUtility)
        // Between the identity row and the fields, so both the singular and the plural branch
        // inherit it: what a string is for is needed before the first keystroke, not behind a panel
        // that costs the queue to open.
        EditorContextStrip(row)
        PresenceHeartbeat(state, row)
        // **A viewer is never told they are a viewer.** `EditorActionBar` returns early without
        // write authority, so a read-only reader gets no save status, no character count and no
        // sentence; the target field stays focusable and selectable on purpose, so the whole
        // experience of not being able to translate is typing into a box and watching nothing
        // happen. The QA page already names its own authority in exactly this shape.
        //
        // `live = false`: this is part of the page at load, not an answer to something the reader
        // just did.
        if (!state.capability.canWriteTranslations) {
            LAlert(
                title = t(
                    "editor.translation_pane.read_only_title",
                    "You can read this translation, not change it",
                ),
                description = t(
                    "editor.translation_pane.read_only_description",
                    "Translating and approving belong to a project's owners, admins and " +
                        "translators. Everything here stays open to read, search and copy.",
                ),
                modifier = Modifier.attr("data-editor-read-only-notice", "true"),
                variant = LAlertVariant.INFO,
            )
        }
        val conflict = state.conflicts[row.key.id]
        if (state.recoveredDrafts[row.key.id] == true && conflict == null) {
            LAlert(
                title = t("editor.translation_pane.draft_recovered", "Draft recovered"),
                description = t(
                    "editor.translation_pane.draft_recovered_description",
                    "We restored unsaved work from this browser. Review it, then save when ready.",
                ),
                modifier = Modifier.attr("data-editor-recovered-draft", "true"),
                variant = LAlertVariant.INFO,
            )
        }
        if (conflict != null) {
            ConflictCard(state, conflict)
        } else if (shapeChanged && (draft.sourceSnapshot == null || draft.retainedShape() == EditorDraftShape.MIXED)) {
            // The complete original values are rendered by the recovery surface below. Unknown
            // source history must not be presented as an invented source for these old fields.
        } else if (editingRow.key.isPlural) {
            EditorPluralFields(
                state = state,
                row = editingRow,
                draft = draft,
                findings = findings,
                onCopySource = onCopySource,
                onInsertText = onInsertText,
                onOpenUtility = onOpenUtility,
                readOnly = shapeChanged,
            )
        } else {
            EditorSingularFields(
                state = state,
                row = editingRow,
                draft = draft,
                findings = findings,
                onCopySource = onCopySource,
                onInsertText = onInsertText,
                onOpenUtility = onOpenUtility,
                readOnly = shapeChanged,
            )
        }
        EditorSourceReviewSurface(state, row, draft)
    }
}

@Composable
private fun EditorContentFrame(
    sidePaddingPx: Int,
    content: @Composable () -> Unit,
) {
    Column(
        EditorFieldContentStyle.toModifier()
            .padding(leftRight = sidePaddingPx.px)
            .attr("data-editor-content", "true"),
    ) {
        content()
    }
}

@Composable
private fun PresenceHeartbeat(state: TranslationEditorState, row: KeyRow) {
    LaunchedEffect(row.key.id, state.locale) {
        state.heartbeatPresence(row.key.id)
        while (true) {
            kotlinx.coroutines.delay(PRESENCE_HEARTBEAT_MS)
            state.heartbeatPresence(row.key.id)
        }
    }
    DisposableEffect(row.key.id, state.locale) {
        onDispose { state.heartbeatPresence(row.key.id, released = true) }
    }
}

@Composable
private fun EditorSkeleton(sidePaddingPx: Int) {
    EditorContentFrame(sidePaddingPx) {
        // Three bars, one sentence. Each `LSkeleton` defaults to its own `role="status"`.
        Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
            Text(t("editor.translation_pane.loading_the_editor", "Loading the editor"))
        }
        LSkeleton(width = 42.percent, height = 28.px, label = null)
        LSkeleton(height = 64.px, label = null)
        LSkeleton(height = 120.px, label = null)
    }
}

@Composable
private fun EmptyEditor(
    state: TranslationEditorState,
    sidePaddingPx: Int,
) {
    val hasNoKeysAtAll = (state.project?.keyCount ?: 0) == 0
    val canImport = state.capability.canWriteTranslations && state.canEditStructure
    val router = rememberPageContext().router
    EditorContentFrame(sidePaddingPx) {
        when {
            hasNoKeysAtAll -> LEmptyState(
                title = t("editor.translation_pane.nothing_to_translate_yet", "Nothing to translate yet"),
                description = if (canImport) {
                    t("editor.translation_pane.this_project_has_no_keys_in", "This project has no keys in it. Import a file, or push them from your repository with `lingvo push`.")
                } else {
                    t("editor.translation_pane.no_keys_are_available_to_translate", "No keys are available to translate yet. They will appear here after an owner or admin imports or pushes them.")
                },
                modifier = EmptyEditorStyle.toModifier(),
                icon = LEmptyStateIcon.LANGUAGE,
                action = {
                    if (canImport) {
                        state.project?.slug?.let { slug ->
                            LButton(t("editor.translation_pane.import_a_file", "Import a file"), {
                                router.tryRoutingTo("/app/projects/$slug/data")
                            })
                        }
                    }
                },
            )
            state.total == 0 -> LEmptyState(
                title = t("editor.translation_pane.no_strings_match_these_filters", "No keys match these filters"),
                // The same sentence the queue beside this pane writes. It used to say only
                // "Nothing in de matches what you have narrowed to.", so the reader was told which
                // pane was empty by the pane that was empty, and never which of four live filters
                // did it.
                description = narrowestEditorFilterDescription(
                    locale = state.locale,
                    search = state.search,
                    tag = state.tag,
                    fileId = state.fileId,
                    selectedStateIndex = EditorStateFilter.entries
                        .indexOf(state.stateFilter)
                        .coerceAtLeast(0),
                    review = state.review,
                    stale = state.stale,
                ),
                modifier = EmptyEditorStyle.toModifier(),
                icon = LEmptyStateIcon.LANGUAGE,
                action = {
                    LButton(
                        t("editor.translation_pane.clear_filters", "Clear filters"),
                        state::clearFilters,
                        variant = LButtonVariant.SECONDARY,
                    )
                },
            )
            else -> LEmptyState(
                title = t("editor.translation_pane.choose_a_string", "Choose a key"),
                description = if (state.capability.canWriteTranslations) {
                    t("editor.translation_pane.pick_one_from_the_list_to", "Pick one from the list to start translating.")
                } else {
                    t("editor.translation_pane.pick_one_from_the_list_to_2", "Pick one from the list to view its translation.")
                },
                modifier = EmptyEditorStyle.toModifier(),
                icon = LEmptyStateIcon.LANGUAGE,
                action = {},
            )
        }
    }
}

internal const val PRESENCE_HEARTBEAT_MS = 20_000L

@Composable
internal fun SourceOnlyPluralDisclosure(
    panels: List<EditorSourceOnlyPluralModel>,
    direction: String,
) {
    if (panels.isEmpty()) return
    var open by remember(panels) { mutableStateOf(false) }
    Button(
        ExtraPluralButtonStyle.toModifier().toAttrs {
            type(ButtonType.Button)
            attr("aria-expanded", open.toString())
            onClick { open = !open }
        },
    ) {
        // `(s)` is a parenthetical apology for not knowing the reader's grammar, in the product
        // whose landing copy sells knowing it. Arabic has six categories, so this count reaches
        // four here and the English "one or many" split is not the only shape it takes.
        Text(
            if (open) {
                t("editor.translation_pane.hide_source_only_forms", "Hide source-only forms")
            } else {
                tPlural(
                    "editor.translation_pane.count.source_only_form",
                    panels.size,
                    "Show {count} source-only form",
                    "Show {count} source-only forms",
                )
            },
        )
    }
    if (open) {
        Column(Modifier.fillMaxWidth().gap(Space.MD).padding(top = Space.SM)) {
            panels.forEach { panel ->
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    Div(EditorSectionHeadingStyle.toModifier().toAttrs()) {
                        Text(panel.categoryLabel)
                    }
                    PlaceholderText(
                        value = panel.source,
                        modifier = SourceTextStyle.toModifier().fillMaxWidth(),
                        direction = direction,
                    )
                }
            }
        }
    }
}
